import { BroadcastChannel, BroadcastChannelOptions } from 'broadcast-channel';
import { useCallback, useEffect, useRef } from 'react';

export type UseBroadcastChannelOptions = Omit<BroadcastChannelOptions, 'node'>;

export function useBroadcastChannel<T>(channelName: string, options?: UseBroadcastChannelOptions) {
  const broadcastChannelRef = useRef<BroadcastChannel<T> | null>(null);
  const onMessageRef = useRef<((message: T) => void) | null>(null);

  const handlePostMessage = useCallback((message: T) => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage(message);
    }
  }, []);

  const handleOnMessage = useCallback((messageCallback: (message: T) => void) => {
    onMessageRef.current = messageCallback;
  }, []);

  useEffect(() => {
    let mounted = true;
    const channel = new BroadcastChannel<T>(channelName, options);

    const handleMessage = (message: T) => {
      if (!mounted) {
        return;
      }
      onMessageRef.current?.(message);
    };

    channel.onmessage = handleMessage;
    broadcastChannelRef.current = channel;

    return () => {
      mounted = false;
      broadcastChannelRef.current = null;
      channel.close();
    };
  }, [channelName, options]);

  return { postMessage: handlePostMessage, setOnMessage: handleOnMessage };
}
